import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toPng } from 'html-to-image';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Collapse,
  IconButton,
  Link,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  WbSunny as SunIcon,
  Air as WindIcon,
  Opacity as HumidityIcon,
  LocalFireDepartment as FireIcon,
  Navigation as DirectionIcon,
  Place as PlaceIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MyLocation as MyLocationIcon,
  LocationOff as LocationOffIcon,
  Calculate as CalculateIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useSearchParams } from 'react-router-dom';
import { getPublicTenants, getForecast } from '../../api/public';
import { useMobileLayout } from '../../utils/useMobileLayout';
import { fdiCategory, fdiTextOn } from '../../utils/fdiCategory';
import FdiCalculatorDialog from '../../components/forecast/FdiCalculatorDialog';
import ForecastMap from './ForecastMap';

// Default coordinates — used when no FPA is selected or FPA has no coords
const DEFAULT_LATITUDE = -25.624739;
const DEFAULT_LONGITUDE = 28.359040;

// localStorage key for caching selected FPA
const FPA_STORAGE_KEY = 'wildfire_selected_fpa';

const ForecastPage = ({ embedded = false } = {}) => {
  const { isMobile } = useMobileLayout();
  const [searchParams] = useSearchParams();
  const [forecast, setForecast] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedMetric, setSelectedMetric] = useState('fdi');
  const [hourlyDetailsOpen, setHourlyDetailsOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  // Per-hour expand state for the mobile HourCard list.
  const [expandedHours, setExpandedHours] = useState({});
  const toggleHour = (idx) => setExpandedHours((prev) => ({ ...prev, [idx]: !prev[idx] }));

  // FPA state
  const [fpas, setFpas] = useState([]);
  const [selectedFpa, setSelectedFpa] = useState(null);
  const [showFpaDialog, setShowFpaDialog] = useState(false);
  const [pendingFpaId, setPendingFpaId] = useState('');

  // "Use my location" override — when set, takes precedence over selectedFpa.
  const [myLocationCoords, setMyLocationCoords] = useState(null);
  const [locatingMe, setLocatingMe] = useState(false);

  // Share button feedback (clipboard fallback path).
  const [shareToast, setShareToast] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  // Off-screen card we render to PNG when the user taps Share.
  const shareCardRef = useRef(null);

  // Determine coordinates to use for forecast.
  // Priority: my-location override > URL params > selected FPA > defaults.
  const urlLat = searchParams.get('lat');
  const urlLng = searchParams.get('lng');
  const latitude = myLocationCoords?.lat
    ?? (urlLat ? parseFloat(urlLat) : (selectedFpa?.latitude ?? DEFAULT_LATITUDE));
  const longitude = myLocationCoords?.lng
    ?? (urlLng ? parseFloat(urlLng) : (selectedFpa?.longitude ?? DEFAULT_LONGITUDE));

  // Load FPA list and restore cached selection on mount.
  // Priority for picking the initial FPA:
  //   1. ?fpa_id= in the URL (share-link landing) — beats cache, beats prompt.
  //   2. cached selection in localStorage.
  //   3. show the picker dialog (unless URL provides lat/lng).
  useEffect(() => {
    const initFpa = async () => {
      try {
        const data = await getPublicTenants();
        const fpaList = data || [];
        setFpas(fpaList);

        // URL-driven FPA (from a share link) takes precedence over cache.
        const urlFpaId = searchParams.get('fpa_id');
        if (urlFpaId) {
          const fromUrl = fpaList.find((f) => String(f.id) === String(urlFpaId));
          if (fromUrl) {
            setSelectedFpa(fromUrl);
            setPendingFpaId(fromUrl.id);
            localStorage.setItem(FPA_STORAGE_KEY, JSON.stringify(fromUrl));
            return;
          }
        }

        // Restore cached FPA if it still exists in the list
        const cached = localStorage.getItem(FPA_STORAGE_KEY);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const stillExists = fpaList.find((f) => f.id === parsed.id);
            if (stillExists) {
              setSelectedFpa(stillExists);
              setPendingFpaId(stillExists.id);
              return; // Dialog not needed
            }
            // Cached FPA no longer exists — clear and prompt
            localStorage.removeItem(FPA_STORAGE_KEY);
          } catch {
            localStorage.removeItem(FPA_STORAGE_KEY);
          }
        }

        // Only show dialog if we have FPAs to choose from and no URL override
        if (fpaList.length > 0 && !urlLat) {
          setShowFpaDialog(true);
        }
      } catch (err) {
        console.error('Failed to load FPA areas:', err);
      }
    };

    initFpa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch forecast whenever coordinates change
  const fetchForecast = useCallback(async (lat, lng) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getForecast({ latitude: lat, longitude: lng });
      setForecast(data.forecast);
    } catch (err) {
      console.error('Error fetching forecast data:', err);
      // Any failure to load the forecast points users at WeatherSA for the
      // official daily Fire Danger Index in the meantime.
      setError({
        message: 'The weather service is currently unavailable.',
        fdiLink: 'https://www.weathersa.co.za/home/fireindex',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForecast(latitude, longitude);
  }, [latitude, longitude, fetchForecast]);

  // ── FPA dialog handlers ────────────────────────────────────────────────── //

  const handleFpaConfirm = () => {
    if (!pendingFpaId) {
      setShowFpaDialog(false);
      return;
    }
    const fpa = fpas.find((f) => f.id === Number(pendingFpaId));
    if (fpa) {
      setSelectedFpa(fpa);
      localStorage.setItem(FPA_STORAGE_KEY, JSON.stringify(fpa));
    }
    setShowFpaDialog(false);
  };

  const handleFpaSkip = () => {
    setShowFpaDialog(false);
  };

  const handleChangeFpa = (e) => {
    const val = e.target.value;
    if (!val) {
      setSelectedFpa(null);
      localStorage.removeItem(FPA_STORAGE_KEY);
      return;
    }
    const fpa = fpas.find((f) => f.id === Number(val));
    if (fpa) {
      setSelectedFpa(fpa);
      setPendingFpaId(fpa.id);
      localStorage.setItem(FPA_STORAGE_KEY, JSON.stringify(fpa));
    }
  };

  // ── Utility functions ──────────────────────────────────────────────────── //

  const handleDayChange = (_event, newValue) => {
    setSelectedDay(newValue);
  };

  const handleMetricChange = (event) => {
    setSelectedMetric(event.target.value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getWindDirection = (degrees) => {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  // FDI category lookup now lives in utils/fdiCategory.js (shared with the
  // calculator dialog + status banner). Local alias keeps existing JSX terse.
  const getFdiCategory = fdiCategory;

  const getChartData = () => {
    if (!forecast || !forecast[selectedDay]) return [];
    return forecast[selectedDay].hourly.map((hour) => ({
      time: formatTime(hour.timestamp),
      temperature: hour.temperature,
      humidity: hour.humidity,
      windSpeed: hour.wind_speed,
      windGusts: hour.wind_gusts,
      windDirection: hour.wind_direction,
      fdi: hour.fire_danger_index,
    }));
  };

  const getDayAverage = (metric) => {
    if (!forecast || !forecast[selectedDay]) return null;
    const hourly = forecast[selectedDay].hourly;
    const values = hourly
      .map((h) => {
        if (metric === 'fdi') return h.fire_danger_index;
        if (metric === 'temperature') return h.temperature;
        if (metric === 'humidity') return h.humidity;
        if (metric === 'windSpeed') return h.wind_speed;
        if (metric === 'windGusts') return h.wind_gusts;
        return null;
      })
      .filter((v) => v != null);
    if (values.length === 0) return null;
    return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
  };

  const getMetricLabel = (metric) => {
    switch (metric) {
      case 'fdi': return 'Fire Danger Index';
      case 'temperature': return 'Temperature (°C)';
      case 'humidity': return 'Humidity (%)';
      case 'windSpeed': return 'Wind Speed (km/h)';
      default: return 'Fire Danger Index';
    }
  };

  const getMetricColor = (metric) => {
    switch (metric) {
      case 'fdi': return '#f44336';
      case 'temperature': return '#e64a19';
      case 'humidity': return '#2196f3';
      case 'windSpeed': return '#4caf50';
      default: return '#f44336';
    }
  };

  // ── Top-level "use my location" + share ──────────────────────────────── //

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setShareToast({ severity: 'warning', text: 'Geolocation is not available in this browser.' });
      return;
    }
    setLocatingMe(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMyLocationCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocatingMe(false);
      },
      (err) => {
        setLocatingMe(false);
        setShareToast({
          severity: 'warning',
          text: err.code === err.PERMISSION_DENIED
            ? 'Location permission denied.'
            : 'Could not fetch your location.',
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const handleStopMyLocation = () => setMyLocationCoords(null);

  // Build a sharable URL that lands the recipient on the same forecast.
  // Prefer fpa_id so the recipient sees the FPA name; fall back to lat/lng
  // for "my-location" mode or default-location browsing.
  const buildShareUrl = () => {
    const base = `${window.location.origin}/forecast`;
    const params = new URLSearchParams();
    if (selectedFpa && !myLocationCoords) {
      params.set('fpa_id', selectedFpa.id);
    } else {
      params.set('lat', latitude.toFixed(5));
      params.set('lng', longitude.toFixed(5));
    }
    return `${base}?${params.toString()}`;
  };

  // Capture the off-screen ShareCard to a PNG and share it.
  // Path of attempts: Web Share API with files → clipboard image → download.
  // Each fallback handles the realistic browser support gap (Safari has no
  // clipboard image write; Firefox has no share files at all).
  const handleShare = async () => {
    if (!shareCardRef.current || isSharing) return;
    const url = buildShareUrl();
    const fileName = `fdi-forecast-${(selectedFpa?.name || 'location').replace(/\s+/g, '-').toLowerCase()}.png`;
    setIsSharing(true);
    try {
      // Render at 2× for retina-sharp output.
      const dataUrl = await toPng(shareCardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
      });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], fileName, { type: 'image/png' });

      // Tenant admins can set a custom FDI share caption in FPA settings; use
      // it verbatim when present, otherwise fall back to the default wording.
      const defaultCaption = selectedFpa
        ? `Today's fire-danger forecast for ${selectedFpa.name}`
        : "Today's fire-danger forecast";
      const customCaption = selectedFpa?.fdi_share_message?.trim();
      const shareData = {
        title: 'Wildfire Forecast',
        text: customCaption || defaultCaption,
        url,
        files: [file],
      };

      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        await navigator.share(shareData);
        return;
      }

      if (navigator.clipboard && typeof window.ClipboardItem !== 'undefined') {
        try {
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': blob }),
          ]);
          setShareToast({ severity: 'success', text: 'Image copied to clipboard.' });
          return;
        } catch (clipErr) {
          // Safari/Firefox throw — fall through to download.
          console.warn('Clipboard image write failed, falling back to download:', clipErr);
        }
      }

      // Final fallback: trigger a download.
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShareToast({ severity: 'success', text: 'Image downloaded.' });
    } catch (err) {
      if (err?.name === 'AbortError') return; // user dismissed share sheet
      console.error('Share failed:', err);
      setShareToast({ severity: 'error', text: 'Could not generate share image.' });
    } finally {
      setIsSharing(false);
    }
  };

  // ── Pending FPA for dialog ─────────────────────────────────────────────── //
  const pendingFpa = pendingFpaId ? fpas.find((f) => f.id === Number(pendingFpaId)) : null;
  const pendingHasNoCoords = pendingFpa && (pendingFpa.latitude == null || pendingFpa.longitude == null);

  // ── Render ─────────────────────────────────────────────────────────────── //

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error.message}
          {error.fdiLink && (
            <>
              {' '}For daily Fire Danger Index information, please see{' '}
              <Link href={error.fdiLink} target="_blank" rel="noopener noreferrer">
                weathersa.co.za/home/fireindex
              </Link>
              .
            </>
          )}
        </Alert>
      </Container>
    );
  }

  if (!forecast || forecast.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info">No forecast data available.</Alert>
      </Container>
    );
  }

  const selectedDayData = forecast[selectedDay];
  const chartData = getChartData();

  return (
    <>
    {/* Hero banner — desktop only; mobile leads with the FDI status banner
        to keep the danger answer above the fold. Hidden when embedded inside
        AppShell (the page header above provides context instead). */}
    {!embedded && (
    <Box
      sx={{
        position: 'relative',
        minHeight: 220,
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/images/hero-landscape.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center 35%',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.65) 100%)',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 3 }}>
        <Typography variant="h3" component="h1" fontWeight="bold" sx={{ color: '#fff', textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
          Weather Forecast
        </Typography>
        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
          {selectedFpa ? `FPA: ${selectedFpa.name}` : 'Fire danger forecast for your area'}
        </Typography>
      </Box>
    </Box>
    )}
    <Container maxWidth="lg" sx={{ py: embedded ? 0 : 4 }}>
      {/* FPA selection dialog */}
      <Dialog open={showFpaDialog} onClose={handleFpaSkip} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Select Your FPA Area</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choose your Fire Protection Association area to see your local weather forecast.
            Your selection will be remembered for next time.
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel>FPA Area</InputLabel>
            <Select
              value={pendingFpaId}
              onChange={(e) => setPendingFpaId(e.target.value)}
              label="FPA Area"
            >
              {fpas.map((fpa) => (
                <MenuItem key={fpa.id} value={fpa.id}>{fpa.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {pendingHasNoCoords && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This FPA area has no coordinates configured. The default location will be used.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFpaSkip}>Skip</Button>
          <Button
            onClick={handleFpaConfirm}
            variant="contained"
            disabled={!pendingFpaId}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header bar — location source + actions */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <PlaceIcon color={myLocationCoords ? 'primary' : 'action'} fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {myLocationCoords
              ? 'Your current location'
              : selectedFpa
                ? selectedFpa.name
                : `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {myLocationCoords ? (
            <MuiTooltip title="Stop using my location">
              <IconButton size="small" onClick={handleStopMyLocation} color="primary">
                <LocationOffIcon />
              </IconButton>
            </MuiTooltip>
          ) : (
            <MuiTooltip title="Use my location">
              <IconButton size="small" onClick={handleUseMyLocation} disabled={locatingMe}>
                {locatingMe ? <CircularProgress size={18} /> : <MyLocationIcon />}
              </IconButton>
            </MuiTooltip>
          )}
          <MuiTooltip title="Share this forecast">
            <span>
              <IconButton size="small" onClick={handleShare} disabled={isSharing}>
                {isSharing ? <CircularProgress size={18} /> : <ShareIcon />}
              </IconButton>
            </span>
          </MuiTooltip>
        </Box>

        {fpas.length > 0 && !myLocationCoords && (
          <FormControl size="small" sx={{ mt: 1, minWidth: { xs: '100%', sm: 240 } }}>
            <InputLabel>Change FPA Area</InputLabel>
            <Select
              value={selectedFpa?.id ?? ''}
              onChange={handleChangeFpa}
              label="Change FPA Area"
              displayEmpty
            >
              <MenuItem value=""><em>Default Location</em></MenuItem>
              {fpas.map((fpa) => (
                <MenuItem key={fpa.id} value={fpa.id}>{fpa.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectedFpa && selectedFpa.latitude == null && !myLocationCoords && (
          <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
            No coordinates for this FPA — using default location.
          </Alert>
        )}
      </Box>

      {/* FDI status banner — color-coded "is it safe to burn today" prompt */}
      {(() => {
        const todayFdi = forecast?.[0]?.fdi_max;
        const cat = fdiCategory(todayFdi);
        return (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: { xs: 1.5, sm: 2 },
              backgroundColor: cat.color,
              color: fdiTextOn(todayFdi),
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <FireIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ opacity: 0.85, lineHeight: 1.2 }}>
                TODAY · FDI {todayFdi ?? '—'}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {cat.level.toUpperCase()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {cat.advice}
              </Typography>
            </Box>
          </Paper>
        );
      })()}

      <Divider sx={{ mb: 3 }} />

      {/* Day selector tabs — abbreviated labels on mobile so 5–7 days fit */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={selectedDay}
          onChange={handleDayChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {forecast.map((day, index) => {
            const d = new Date(day.date);
            const shortLabel = d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
            return (
              <Tab
                key={index}
                label={isMobile ? shortLabel : formatDate(day.date)}
                sx={{ minWidth: { xs: 72, md: 120 }, px: { xs: 1, md: 2 } }}
              />
            );
          })}
        </Tabs>
      </Paper>

      {/* Current day overview — 2×2 on mobile, 1×4 on desktop */}
      <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <SunIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: '#ff9800', mb: 0.5 }} />
              <Typography variant="body2" component="div" color="text.secondary">
                Temperature
              </Typography>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                {selectedDayData.temperature_max}°C
              </Typography>
              <Typography variant="caption" color="text.secondary">
                High {selectedDayData.temperature_max}° · Low {selectedDayData.temperature_min}°
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <HumidityIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: '#2196f3', mb: 0.5 }} />
              <Typography variant="body2" component="div" color="text.secondary">
                Humidity
              </Typography>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                {selectedDayData.humidity_avg}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Average
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <WindIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: '#4caf50', mb: 0.5 }} />
              <Typography variant="body2" component="div" color="text.secondary">
                Wind
              </Typography>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                {selectedDayData.wind_speed_max} km/h
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Gust {selectedDayData.wind_gusts_max ?? '—'} · Avg {selectedDayData.wind_speed_avg}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              {(() => {
                const fdiMax = selectedDayData.fdi_max ?? 0;
                const fdiMin = selectedDayData.fdi_min ?? 0;
                const cat = getFdiCategory(fdiMax);
                return (
                  <>
                    <FireIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: cat.color, mb: 0.5 }} />
                    <Typography variant="body2" component="div" color="text.secondary">
                      Fire Danger
                    </Typography>
                    <Typography variant="h5" sx={{ color: cat.color, fontWeight: 700 }}>
                      {fdiMax}
                    </Typography>
                    <Typography variant="caption" sx={{ color: cat.color, fontWeight: 'bold' }}>
                      {cat.level}
                    </Typography>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Forecast map — overlay forecast conditions on a real map with a
          time-of-day slider. Lets firefighters on scene see the forecast
          without filing a wildfire report. */}
      <Box sx={{ mb: 3 }}>
        <ForecastMap
          latitude={latitude}
          longitude={longitude}
          hourly={selectedDayData.hourly || []}
          /* Long-press the map, then "Forecast here" — re-points the page at
             the pin. Goes through the same state as "Use my location" so it
             wins over the ?lat/?lng params the way a deliberate pick should. */
          onPickLocation={(lat, lng) => setMyLocationCoords({ lat, lng })}
        />
      </Box>

      {/* Hourly forecast chart */}
      <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          mb: 2,
        }}>
          <Typography variant="h6">
            Hourly Forecast — {formatDate(selectedDayData.date)}
          </Typography>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel>Metric</InputLabel>
            <Select
              value={selectedMetric}
              label="Metric"
              onChange={handleMetricChange}
            >
              <MenuItem value="fdi">Fire Danger Index</MenuItem>
              <MenuItem value="temperature">Temperature</MenuItem>
              <MenuItem value="humidity">Humidity</MenuItem>
              <MenuItem value="windSpeed">Wind Speed</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ width: '100%', height: { xs: 220, md: 300 } }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis />
            <Tooltip />
            <Legend />
            {selectedMetric === 'windSpeed' ? (
              <>
                <Line
                  type="monotone"
                  dataKey="windSpeed"
                  stroke="#4caf50"
                  strokeWidth={2}
                  name="Wind Speed (km/h)"
                />
                <Line
                  type="monotone"
                  dataKey="windGusts"
                  stroke="#ff9800"
                  strokeWidth={2}
                  name="Wind Gusts (km/h)"
                />
                {getDayAverage('windSpeed') != null && (
                  <ReferenceLine
                    y={getDayAverage('windSpeed')}
                    stroke="#4caf50"
                    strokeDasharray="5 5"
                    label={{ value: `Avg ${getDayAverage('windSpeed')}`, position: 'right', fontSize: 11 }}
                  />
                )}
                {getDayAverage('windGusts') != null && (
                  <ReferenceLine
                    y={getDayAverage('windGusts')}
                    stroke="#ff9800"
                    strokeDasharray="5 5"
                    label={{ value: `Avg ${getDayAverage('windGusts')}`, position: 'left', fontSize: 11 }}
                  />
                )}
              </>
            ) : (
              <>
                <Line
                  type="monotone"
                  dataKey={selectedMetric}
                  stroke={getMetricColor(selectedMetric)}
                  strokeWidth={2}
                  name={getMetricLabel(selectedMetric)}
                />
                {getDayAverage(selectedMetric) != null && (
                  <ReferenceLine
                    y={getDayAverage(selectedMetric)}
                    stroke={getMetricColor(selectedMetric)}
                    strokeDasharray="5 5"
                    strokeOpacity={0.6}
                    label={{ value: `Avg ${getDayAverage(selectedMetric)}`, position: 'right', fontSize: 11 }}
                  />
                )}
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
        </Box>
      </Paper>

      {/* Hourly details — table on desktop, card list on mobile */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            borderBottom: hourlyDetailsOpen ? 1 : 0,
            borderColor: 'divider',
          }}
          onClick={() => setHourlyDetailsOpen(!hourlyDetailsOpen)}
        >
          <Typography variant="h6">
            Hourly Details - {formatDate(selectedDayData.date)}
          </Typography>
          <IconButton size="small">
            {hourlyDetailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={hourlyDetailsOpen}>
          {isMobile ? (
            <Stack spacing={1} sx={{ p: 1.5 }}>
              {selectedDayData.hourly.map((hour, index) => {
                const cat = getFdiCategory(hour.fire_danger_index);
                const open = !!expandedHours[index];
                return (
                  <Paper
                    key={index}
                    variant="outlined"
                    onClick={() => toggleHour(index)}
                    sx={{ p: 1.25, cursor: 'pointer', '&:active': { backgroundColor: 'action.hover' } }}
                  >
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', gap: 1.25 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 52 }}>
                        {formatTime(hour.timestamp)}
                      </Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                          {hour.temperature}°C · {hour.humidity}% RH
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DirectionIcon sx={{ transform: `rotate(${hour.wind_direction}deg)`, fontSize: 14 }} />
                          <Typography variant="caption" color="text.secondary">
                            {hour.wind_speed} km/h ({getWindDirection(hour.wind_direction)})
                            {hour.wind_gusts != null && ` · gust ${hour.wind_gusts}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={hour.fire_danger_index ?? '—'}
                        size="small"
                        sx={{
                          backgroundColor: cat.color,
                          color: fdiTextOn(hour.fire_danger_index),
                          fontWeight: 700,
                          minWidth: 44,
                        }}
                      />
                    </Box>
                    <Collapse in={open} unmountOnExit>
                      <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Danger level: <Box component="span" sx={{ color: cat.color, fontWeight: 700 }}>{cat.level}</Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Precipitation: {hour.precipitation != null ? `${hour.precipitation} mm` : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {cat.advice}
                        </Typography>
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell align="right">Temp (°C)</TableCell>
                    <TableCell align="right">Humidity (%)</TableCell>
                    <TableCell align="right">Wind (km/h)</TableCell>
                    <TableCell align="right">Gusts (km/h)</TableCell>
                    <TableCell align="right">Wind Dir</TableCell>
                    <TableCell align="right">FDI</TableCell>
                    <TableCell align="right">Danger Level</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedDayData.hourly.map((hour, index) => {
                    const cat = getFdiCategory(hour.fire_danger_index);
                    return (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          {formatTime(hour.timestamp)}
                        </TableCell>
                        <TableCell align="right">{hour.temperature}</TableCell>
                        <TableCell align="right">{hour.humidity}</TableCell>
                        <TableCell align="right">{hour.wind_speed}</TableCell>
                        <TableCell align="right">{hour.wind_gusts ?? '—'}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <DirectionIcon
                              sx={{
                                transform: `rotate(${hour.wind_direction}deg)`,
                                mr: 1,
                                fontSize: 16
                              }}
                            />
                            {getWindDirection(hour.wind_direction)}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {hour.fire_danger_index ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ color: cat.color, fontWeight: 'bold' }}
                          >
                            {cat.level}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Collapse>
      </Paper>

      {/* Manual FDI Calculator — opens as a fullScreen dialog on mobile */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<CalculateIcon />}
          onClick={() => setCalcOpen(true)}
        >
          Calculate FDI manually
        </Button>
      </Box>
      <FdiCalculatorDialog
        open={calcOpen}
        onClose={() => setCalcOpen(false)}
        prefillFromForecast={selectedDayData}
      />
    </Container>

    {/* Off-screen share card — rendered to PNG by handleShare. Fixed width
        and inline styles so the captured image is identical across viewports
        and doesn't depend on the MUI theme being fully resolved at capture. */}
    <Box
      sx={{
        position: 'fixed',
        top: -10000,
        left: 0,
        pointerEvents: 'none',
        zIndex: -1,
      }}
      aria-hidden
    >
      {(() => {
        const locationLabel = myLocationCoords
          ? 'Your current location'
          : selectedFpa
            ? selectedFpa.name
            : `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
        const todayFdi = forecast?.[0]?.fdi_max;
        const todayCat = getFdiCategory(todayFdi);
        const fdiMax = selectedDayData.fdi_max ?? 0;
        const dayCat = getFdiCategory(fdiMax);
        const generatedAt = new Date().toLocaleString('en-ZA', {
          year: 'numeric', month: 'short', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        });
        const siteUrl = `${window.location.origin}/forecast`;
        const sawsUrl = 'https://www.weathersa.co.za/home/fireindex';
        return (
          <div
            ref={shareCardRef}
            style={{
              width: 760,
              padding: 24,
              background: '#ffffff',
              fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
              color: '#1a1a1a',
              boxSizing: 'border-box',
            }}
          >
            {/* Header */}
            <div style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>Wildfire Forecast</div>
              <div style={{ fontSize: 14, color: '#555', marginTop: 4 }}>{locationLabel}</div>
            </div>

            {/* FDI banner (today) */}
            <div style={{
              background: todayCat.color,
              color: fdiTextOn(todayFdi),
              borderRadius: 6,
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginBottom: 20,
            }}>
              <div style={{ fontSize: 36, lineHeight: 1 }}>🔥</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, opacity: 0.9, letterSpacing: 0.5 }}>
                  TODAY · FDI {todayFdi ?? '—'}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>
                  {todayCat.level.toUpperCase()}
                </div>
                <div style={{ fontSize: 14, marginTop: 4, opacity: 0.95 }}>
                  {todayCat.advice}
                </div>
              </div>
            </div>

            {/* Selected-day label */}
            <div style={{ fontSize: 13, color: '#777', marginBottom: 8 }}>
              {formatDate(selectedDayData.date)}
            </div>

            {/* 4 metric cards */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Temperature', value: `${selectedDayData.temperature_max}°C`, sub: `High ${selectedDayData.temperature_max}° · Low ${selectedDayData.temperature_min}°`, color: '#ff9800' },
                { label: 'Humidity', value: `${selectedDayData.humidity_avg}%`, sub: 'Average', color: '#2196f3' },
                { label: 'Wind', value: `${selectedDayData.wind_speed_max} km/h`, sub: `Gust ${selectedDayData.wind_gusts_max ?? '—'} · Avg ${selectedDayData.wind_speed_avg}`, color: '#4caf50' },
                { label: 'Fire Danger', value: `${fdiMax}`, sub: dayCat.level, color: dayCat.color, valueColor: dayCat.color, subColor: dayCat.color },
              ].map((c) => (
                <div
                  key={c.label}
                  style={{
                    flex: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 6,
                    padding: '12px 8px',
                    textAlign: 'center',
                    background: '#fafafa',
                  }}
                >
                  <div style={{ width: 10, height: 10, borderRadius: 5, background: c.color, margin: '0 auto 6px' }} />
                  <div style={{ fontSize: 12, color: '#777' }}>{c.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: c.valueColor || '#1976d2', marginTop: 2 }}>
                    {c.value}
                  </div>
                  <div style={{ fontSize: 11, color: c.subColor || '#777', marginTop: 2, fontWeight: c.subColor ? 700 : 400 }}>
                    {c.sub}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{
              borderTop: '1px solid #e0e0e0',
              paddingTop: 12,
              fontSize: 12,
              color: '#555',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}>
              <div>Generated {generatedAt}</div>
              <div style={{ textAlign: 'right' }}>
                <div>Live forecast: <span style={{ color: '#1976d2' }}>{siteUrl}</span></div>
                <div>SAWS FDI: <span style={{ color: '#1976d2' }}>{sawsUrl}</span></div>
              </div>
            </div>
          </div>
        );
      })()}
    </Box>

    <Snackbar
      open={Boolean(shareToast)}
      autoHideDuration={3500}
      onClose={() => setShareToast(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      {shareToast ? (
        <Alert severity={shareToast.severity} onClose={() => setShareToast(null)} sx={{ width: '100%' }}>
          {shareToast.text}
        </Alert>
      ) : null}
    </Snackbar>
    </>
  );
};

export default ForecastPage;
